import { useState } from 'preact/hooks';
import { EXPENSES, EXPENSE_ORDER } from '../../content/expenses';
import { fmtEur, fmtPct } from '../../engine/format';
import { t } from '../../engine/i18n';
import type { ExpenseId, GameState } from '../../engine/state';
import { update } from '../../engine/store';
import {
  SCHOOL_FEE,
  atTodayPrices,
  deferredLoanPayment,
  fixedExpenses,
  inPrivateSchool,
  isLoanActive,
  livretOpen,
  monthlyIncome,
  monthlyLoanPayments,
} from '../../game/economy';
import { BANK, type BankLoanKind, bankLoanKind, bankLoanPreview, bankLoanRefusal, takeBankLoan } from '../../game/bank';
import { Card, Line } from '../components';
import { peaMonthlyAmount, togglePeaMonthly } from '../../game/pea';
import { MUTUELLE_LABEL, PERK_HINT, subscriptionLabel } from '../labels';

export function BudgetTab({ state }: { state: GameState }) {
  const income = monthlyIncome(state);
  const needs = fixedExpenses(state);
  const loans = monthlyLoanPayments(state);
  const rest = income - needs - loans - state.budget.leisure - state.budget.autoSaving - peaMonthlyAmount(state);
  const sliderMax = Math.max(100, Math.round(income / 10) * 10);
  // Hors de chez les parents, les lignes du logement se rangent sous « Habitation », à la place de la première.
  const ownHome = state.housing !== null && state.housing !== 'parents';
  const firstHousing = ownHome ? HOUSING_LINES.find((id) => state.expenses[id]) : undefined;

  return (
    <div class="grid">
      <Card title={t('budget.title')}>
        <Line label={t('budget.income')} value={fmtEur(income)} tone="good" />
        <h3 class="subtitle">{t('budget.needs')}</h3>
        {EXPENSE_ORDER.filter((id) => state.expenses[id] && !(ownHome && HOUSING_LINES.includes(id) && id !== firstHousing)).map(
          (id) =>
            id === 'abonnements' ? (
              <SubscriptionsLine key={id} state={state} />
            ) : id === firstHousing ? (
              <HabitationLine key={id} state={state} />
            ) : (
              <Line
                key={id}
                label={
                  id === 'mutuelle'
                    ? t('expense.mutuelleLevel', { level: t(MUTUELLE_LABEL[state.mutuelleLevel]) })
                    : t(EXPENSES[id].label)
                }
                value={fmtEur(state.expenses[id]?.amount ?? 0)}
              />
            ),
        )}
        <Line label={t('budget.needs')} value={fmtEur(needs)} tone="strong" />
        {state.expenses.mutuelle && <p class="hint">{t('budget.mutuelleHint')}</p>}
        {inPrivateSchool(state) && (
          <p class="warning">{t('budget.schoolFees', { amount: fmtEur(atTodayPrices(state, SCHOOL_FEE)) })}</p>
        )}

        <h3 class="subtitle">{t('budget.wants')}</h3>
        <Slider
          value={state.budget.leisure}
          max={sliderMax}
          label={t('budget.wants')}
          onChange={(value) => update((s) => (s.budget.leisure = value))}
        />
        <p class="muted">{t('budget.wantsHint')}</p>
        {Object.entries(state.perks)
          .filter(([id]) => PERK_HINT[id])
          .map(([id, perk]) => (
            <p key={id} class="hint">
              {t(PERK_HINT[id], { amount: fmtEur(perk.leisure), points: perk.wellbeing })}
            </p>
          ))}
        {state.priceLevel > 1.0001 && (
          <p class="hint">{t('budget.wantsInflation', { pct: fmtPct(state.priceLevel - 1) })}</p>
        )}

        <h3 class="subtitle">{livretOpen(state) ? t('budget.savings') : t('budget.savingsPrologue')}</h3>
        <Slider
          value={state.budget.autoSaving}
          max={sliderMax}
          label={t('budget.savings')}
          onChange={(value) => update((s) => (s.budget.autoSaving = value))}
        />
        <p class="muted">{t('budget.savingsHint')}</p>
        {state.pea.open && (
          <>
            <h3 class="subtitle">{t('budget.peaMonthly')}</h3>
            <Slider
              value={state.pea.monthly}
              max={sliderMax}
              label={t('budget.peaMonthly')}
              onChange={(value) => update((s) => (s.pea.monthly = value))}
            />
            <p class="muted">{t('budget.peaMonthlyHint')}</p>
            {state.pea.monthly > 0 && (
              <>
                {state.pea.monthlyPaused && <p class="warning">{t('budget.peaPaused')}</p>}
                <button onClick={() => update(togglePeaMonthly)}>
                  {t(state.pea.monthlyPaused ? 'invest.resume' : 'invest.pause')}
                </button>
              </>
            )}
          </>
        )}

        {loans > 0 && <Line label={t('budget.loans')} value={fmtEur(-loans)} />}
        <Line label={t('budget.rest')} value={fmtEur(rest)} tone={rest < 0 ? 'bad' : 'strong'} />
        {rest < 0 && <p class="warning">{t('budget.negative')}</p>}
      </Card>

      <Card>
        <p>{t('budget.rule')}</p>
        <p class="muted">
          {t('budget.ruleValues', {
            needs: fmtEur(income * 0.5),
            wants: fmtEur(income * 0.3),
            savings: fmtEur(income * 0.2),
          })}
        </p>
      </Card>

      <Card title={t('budget.loans')}>
        {state.loans.length === 0 ? (
          <p class="muted">{t('budget.noLoans')}</p>
        ) : (
          <ul class="plain-list">
            {state.loans.map((loan) => (
              <li key={loan.id}>
                {isLoanActive(state, loan)
                  ? t(loan.insuranceMonthly ? 'budget.loanLineInsured' : 'budget.loanLine', {
                      label: t(loan.label),
                      payment: fmtEur(loan.monthlyPayment + (loan.insuranceMonthly ?? 0)),
                      insurance: fmtEur(loan.insuranceMonthly ?? 0),
                      months: loan.monthsLeft,
                      interest: fmtEur(loan.totalInterest),
                    })
                  : t('budget.loanDeferred', {
                      label: t(loan.label),
                      balance: fmtEur(loan.balance),
                      payment: fmtEur(deferredLoanPayment(state, loan)),
                      months: loan.monthsLeft,
                    })}
              </li>
            ))}
          </ul>
        )}
        <BankLoans state={state} />
      </Card>
    </div>
  );
}

function Slider({
  value,
  max,
  label,
  onChange,
}: {
  value: number;
  max: number;
  label: string;
  onChange: (value: number) => void;
}) {
  return (
    <div class="slider">
      <input
        type="range"
        min={0}
        max={max}
        step={10}
        value={Math.min(value, max)}
        aria-label={label}
        onInput={(event) => onChange(Number((event.target as HTMLInputElement).value))}
      />
      <span class="slider-value">{t('common.perMonth', { amount: fmtEur(value) })}</span>
    </div>
  );
}

/** Les lignes du logement, rangées sous « Habitation » quand on vit chez soi. */
const HOUSING_LINES: ExpenseId[] = ['logement', 'taxeFonciere', 'copropriete', 'charges', 'energie', 'assurance'];

interface Detail {
  key: string;
  label: string;
  amount: number;
}

/** Une ligne du budget qui se déplie au clic pour montrer son détail. */
function ExpandableLine({ label, title, items, empty }: { label: string; title: string; items: Detail[]; empty: string }) {
  const [open, setOpen] = useState(false);
  const total = items.reduce((sum, item) => sum + item.amount, 0);
  return (
    <>
      <button type="button" class="line line-toggle" aria-expanded={open} title={title} onClick={() => setOpen(!open)}>
        <span class="line-label">
          {label} <span aria-hidden="true">{open ? '▾' : '▸'}</span>
        </span>
        <span class="line-value">{fmtEur(total)}</span>
      </button>
      {open && (
        <ul class="sublines">
          {items.length === 0 && <li class="muted">{empty}</li>}
          {items.map((item) => (
            <li key={item.key} class="line">
              <span class="line-label">{item.label}</span>
              <span class="line-value">{fmtEur(item.amount)}</span>
            </li>
          ))}
        </ul>
      )}
    </>
  );
}

/** La ligne « Abonnements », qui se déplie pour montrer chaque abonnement en cours. */
function SubscriptionsLine({ state }: { state: GameState }) {
  const items = state.subscriptions.map((sub, index) => ({
    key: `${sub.id}-${index}`,
    label: subscriptionLabel(sub),
    amount: sub.amount,
  }));
  return (
    <ExpandableLine
      label={t('expense.abonnements')}
      title={t('budget.subscriptionsShow')}
      items={items}
      empty={t('budget.noSubscription')}
    />
  );
}

/** La ligne « Habitation » d'un logement à soi : le loyer, les charges, l'électricité et l'assurance. */
function HabitationLine({ state }: { state: GameState }) {
  const items = HOUSING_LINES.filter((id) => state.expenses[id]).map((id) => ({
    key: id,
    label: id === 'logement' ? t('budget.rent') : t(EXPENSES[id].label),
    amount: state.expenses[id]?.amount ?? 0,
  }));
  return <ExpandableLine label={t('budget.habitation')} title={t('budget.habitationShow')} items={items} empty="" />;
}

/** Les montants proposés par la banque, par tranches de 1 000 €. */
function amountsOf(kind: BankLoanKind): number[] {
  const rules = kind === 'student' ? BANK.student : BANK.personal;
  const amounts: number[] = [];
  for (let amount = rules.min; amount <= rules.max; amount += BANK.step) amounts.push(amount);
  return amounts;
}

/** Le guichet de la banque : un prêt étudiant pendant l'école privée, un prêt personnel une fois au travail. */
function BankLoans({ state }: { state: GameState }) {
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState(5000);
  const [months, setMonths] = useState(36);
  const kind = bankLoanKind(state);
  if (!kind) return null;
  if (!open) return <button onClick={() => setOpen(true)}>{t('bank.request')}</button>;
  const student = kind === 'student';
  const rules = student ? BANK.student : BANK.personal;
  const value = Math.min(rules.max, Math.max(rules.min, amount));
  const preview = bankLoanPreview(state, kind, value, months);
  const refusal = bankLoanRefusal(state, kind, value, months);
  const summary = {
    amount: fmtEur(value),
    debt: fmtEur(preview.debt),
    payment: fmtEur(preview.payment),
    months: preview.months,
    total: fmtEur(preview.total),
    interest: fmtEur(preview.interest),
  };
  return (
    <div class="offer">
      <p class="eyebrow">{t(student ? 'bank.student' : 'bank.personal')}</p>
      <p class="hint">
        {t(student ? 'bank.studentTerms' : 'bank.personalTerms', {
          rate: fmtPct(rules.rate),
          cap: fmtEur(BANK.student.cap),
        })}
      </p>
      <label class="field">
        <span class="muted">{t('bank.amount')}</span>
        <select value={value} onChange={(event) => setAmount(Number((event.target as HTMLSelectElement).value))}>
          {amountsOf(kind).map((item) => (
            <option key={item} value={item}>
              {fmtEur(item)}
            </option>
          ))}
        </select>
      </label>
      {!student && (
        <label class="field">
          <span class="muted">{t('bank.duration')}</span>
          <select value={months} onChange={(event) => setMonths(Number((event.target as HTMLSelectElement).value))}>
            {BANK.personal.durations.map((item) => (
              <option key={item} value={item}>
                {t('bank.months', { count: item })}
              </option>
            ))}
          </select>
        </label>
      )}
      <p>{t(student ? 'bank.studentSummary' : 'bank.personalSummary', summary)}</p>
      {refusal && <p class="warning">{t(refusal.key, refusal.params)}</p>}
      <div class="button-row">
        <button
          class="button-primary"
          disabled={refusal !== null}
          onClick={() => {
            update((s) => takeBankLoan(s, kind, value, months));
            setOpen(false);
          }}
        >
          {t('bank.sign')}
        </button>
        <button onClick={() => setOpen(false)}>{t('invest.cancel')}</button>
      </div>
    </div>
  );
}
